import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Loader2, Sparkles, AlertTriangle, Check } from 'lucide-react';

const input = `w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm
               text-white placeholder-slate-500 focus:outline-none focus:border-amber-400/70`;
const small = `rounded-lg border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white
               focus:outline-none focus:border-amber-400/70`;

const WINDOWS = ['any', 'morning', 'midday', 'evening'];

/**
 * Admin → Ritual Cards → Draft with AI.
 * It reads one of his program PDFs and proposes a card per practice. Nothing is saved until
 * he ticks the ones he wants: his wording, his call.
 */
export const RitualDraftPanel = ({ sections, onCreated }) => {
  const [documents, setDocuments] = useState([]);
  const [contentId, setContentId] = useState('');
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);   // { cards, flags, source_title }
  const [rows, setRows] = useState([]);         // [{ ...card, keep }]
  const [folderId, setFolderId] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/ritual-cards/source-documents`)
      .then((r) => setDocuments(r.data.modules || []))
      .catch(() => toast.error('The document list could not be loaded'));
  }, []);

  const chosen = documents.flatMap((m) => m.documents.map((d) => ({ ...d, module: m })))
    .find((d) => d.content_id === contentId);

  const draft = async () => {
    setDrafting(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/ritual-cards/draft/${contentId}`);
      setResult(data);
      setRows((data.cards || []).map((c) => ({ ...c, keep: true })));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'That document could not be drafted');
    } finally {
      setDrafting(false);
    }
  };

  const patch = (index, changes) => setRows((prev) =>
    prev.map((row, i) => (i === index ? { ...row, ...changes } : row)));

  const setAll = useCallback((keep) => setRows((prev) => prev.map((r) => ({ ...r, keep }))), []);

  const kept = rows.filter((r) => r.keep);

  const create = async () => {
    setSaving(true);
    try {
      const payload = kept.map(({ keep, ...card }) => ({
        ...card,
        duration_minutes: card.duration_minutes ? Number(card.duration_minutes) : null,
        source_folder_id: folderId === '__none__' ? null : (folderId || card.source_folder_id || null),
      }));
      const { data } = await axios.post(`${API}/admin/ritual-cards/bulk`, payload);
      toast.success(`${data.count} card${data.count === 1 ? '' : 's'} created`);
      setResult(null);
      setRows([]);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Those cards could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const folders = (sections.modules || [])
    .find((m) => m.id === chosen?.module?.module_id)?.folders || [];

  return (
    <div className="rounded-xl border border-amber-500/25 bg-slate-900/60 p-5 space-y-4"
         data-testid="ritual-draft-panel">
      <div>
        <h3 className="text-sm font-semibold text-white">Draft cards from a document</h3>
        <p className="text-[11px] text-slate-400 mt-1">
          It reads the PDF and proposes one card per practice, in your words. A length is only
          filled in where the document names one, and Anchor and Landing are left for the member.
          Nothing is saved until you press Create.
        </p>
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
        <label className="block space-y-1">
          <span className="text-xs text-slate-300">Document</span>
          <select className={input} value={contentId}
                  onChange={(e) => { setContentId(e.target.value); setResult(null); }}
                  data-testid="draft-document-select">
            <option value="">Choose an exercise or supplement…</option>
            {documents.map((m) => (
              <optgroup key={m.module_id} label={m.module_title}>
                {m.documents.map((d) => (
                  <option key={d.content_id} value={d.content_id}>
                    {d.title}{d.cards ? ` — ${d.cards} card${d.cards === 1 ? '' : 's'} already` : ''}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>
        <button onClick={draft} disabled={!contentId || drafting}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2
                           text-sm font-semibold text-slate-950 hover:bg-amber-400
                           disabled:opacity-40 transition-colors"
                data-testid="draft-cards-btn">
          {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {drafting ? 'Reading…' : 'Draft cards'}
        </button>
      </div>

      {drafting && (
        <p className="text-[11px] text-slate-400" data-testid="draft-working-note">
          Reading the document and writing the cards — a long supplement takes up to a minute.
        </p>
      )}

      {result && (
        <div className="space-y-3" data-testid="draft-review-list">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-slate-300">
              <span className="text-white font-semibold">{rows.length}</span> cards from{' '}
              {result.source_title} · <span className="text-white">{kept.length}</span> ticked
            </p>
            <div className="flex gap-2">
              <button onClick={() => setAll(true)} className="text-[11px] text-slate-400
                       hover:text-white transition-colors" data-testid="draft-select-all">
                Tick all
              </button>
              <button onClick={() => setAll(false)} className="text-[11px] text-slate-400
                       hover:text-white transition-colors" data-testid="draft-select-none">
                Untick all
              </button>
            </div>
          </div>

          {(result.flags || []).length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 space-y-2"
                 data-testid="draft-flags">
              <p className="flex items-center gap-1.5 text-xs text-amber-200">
                <AlertTriangle className="w-3.5 h-3.5" /> Worth a look before you create these
              </p>
              {result.flags.map((flag, i) => (
                <p key={i} className="text-[11px] text-amber-100/80">
                  {flag.reason}
                  {flag.quote && <span className="block text-amber-100/50">“{flag.quote}”</span>}
                </p>
              ))}
            </div>
          )}

          <label className="block space-y-1 max-w-sm">
            <span className="text-xs text-slate-300">Section gate for every card</span>
            <select className={input} value={folderId} onChange={(e) => setFolderId(e.target.value)}
                    data-testid="draft-folder-select">
              <option value="">
                Keep the document's own section{chosen?.section ? ` (${chosen.section})` : ''}
              </option>
              <option value="__none__">No gate — visible to every member</option>
              {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>

          <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1">
            {rows.map((row, index) => (
              <div key={index}
                   className={`rounded-lg border px-3 py-2.5 transition-colors ${
                     row.keep ? 'border-slate-600 bg-slate-900/70' : 'border-slate-800 bg-slate-900/20 opacity-55'}`}
                   data-testid={`draft-row-${index}`}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={row.keep} className="mt-2 accent-amber-400"
                         onChange={(e) => patch(index, { keep: e.target.checked })}
                         data-testid={`draft-keep-${index}`} />
                  <div className="flex-1 min-w-0 space-y-2">
                    <input className={input} value={row.title}
                           onChange={(e) => patch(index, { title: e.target.value })}
                           data-testid={`draft-title-${index}`} />
                    <p className="text-[11px] leading-relaxed text-slate-400">{row.description}</p>
                    {row.intention && (
                      <p className="text-[11px] text-cyan-200/70">Intention: {row.intention}</p>
                    )}
                    {(row.prompts || []).length > 0 && (
                      <p className="text-[11px] text-slate-500">
                        {row.prompts.length} prompt{row.prompts.length === 1 ? '' : 's'}: {row.prompts.join(' · ')}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <input className={`${small} w-32`} placeholder="Family"
                             value={row.category}
                             onChange={(e) => patch(index, { category: e.target.value })}
                             data-testid={`draft-category-${index}`} />
                      <input className={`${small} w-44`} placeholder="Sub-category"
                             value={row.subcategory || ''}
                             onChange={(e) => patch(index, { subcategory: e.target.value })}
                             data-testid={`draft-subcategory-${index}`} />
                      <select className={small} value={row.suggested_window}
                              onChange={(e) => patch(index, { suggested_window: e.target.value })}
                              data-testid={`draft-window-${index}`}>
                        {WINDOWS.map((w) => (
                          <option key={w} value={w}>{w === 'any' ? 'Any window' : w}</option>
                        ))}
                      </select>
                      <input className={`${small} w-28`} type="number" min="1" placeholder="Minutes"
                             value={row.duration_minutes ?? ''}
                             onChange={(e) => patch(index, { duration_minutes: e.target.value })}
                             data-testid={`draft-duration-${index}`} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <button onClick={create} disabled={saving || kept.length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-4 py-2
                             text-sm font-semibold text-slate-950 hover:bg-amber-400
                             disabled:opacity-40 transition-colors"
                  data-testid="create-drafted-cards-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create {kept.length} card{kept.length === 1 ? '' : 's'}
          </button>
        </div>
      )}
    </div>
  );
};

export default RitualDraftPanel;
